import { toDisplayDate } from '@/utils/dateString';
import React, { useState } from 'react';
import {
  User,
  Phone,
  Briefcase,
  ChevronDown,
  ChevronUp,
  Edit3,
  Users,
  Shield,
  CheckCircle,
  GraduationCap,
  FileText,
  Eye,
  Download,
} from 'lucide-react';
import { StaffChangeRequestModal } from './StaffChangeRequestModal';
import { ChangeRequestsSection } from './ChangeRequestsSection';
import { countries as countriesData } from '@/data/countries';
import type { SignatureStatus } from '@/services/validationService';
import type { CommonProfileFields } from '@/types/profile.types';
import { emergencyRelationLabel } from '@/shared/constants/emergencyRelation';

// Document interface for display
interface ProfileDocument {
  id: string;
  documentType: string;
  fileName: string;
  fileUrl: string;
  isVerified?: boolean;
  verifiedAt?: string;
}

interface StaffProfileViewProps {
  data: Partial<CommonProfileFields & Record<string, unknown>>;
  showChangeRequest?: boolean;
  isProfessor?: boolean;
  /** Signature status for displaying electronic signature traceability */
  signatureStatus?: SignatureStatus;
  /** Department name (resolved from ID) */
  departementName?: string;
  /** Grade name (resolved from ID) */
  gradeName?: string;
  /** Diplome name (resolved from ID) */
  diplomeName?: string;
  /** Payroll campus name (resolved from ID) */
  payrollCampusName?: string;
  /** Documents uploaded by the user */
  documents?: ProfileDocument[];
  /** Whether the staff member is archived (WITHDRAWN) */
  isArchived?: boolean;
  /** Date when the staff member was archived */
  archivedAt?: string;
}

// Helper to format values for display
const formatValue = (value: unknown, fallback = '—'): string => {
  if (value === undefined || value === null || value === '') return fallback;
  return String(value);
};

// Helper to get country name from code
const getCountryName = (codeOrName?: string): string => {
  if (!codeOrName) return '—';
  // Try to find by code first
  const foundByCode = countriesData.find(c => c.code === codeOrName);
  if (foundByCode) return foundByCode.nameFr;
  // If not found by code, it might already be a name
  const foundByName = countriesData.find(c =>
    c.nameFr.toLowerCase() === codeOrName.toLowerCase()
  );
  if (foundByName) return foundByName.nameFr;
  // Return the original value as-is (it's an unknown country)
  return codeOrName;
};

// Country phone codes for display
const COUNTRY_PHONE_CODES: Record<string, { dialCode: string; flag: string }> = {
  'MA': { dialCode: '+212', flag: '🇲🇦' },
  'DZ': { dialCode: '+213', flag: '🇩🇿' },
  'TN': { dialCode: '+216', flag: '🇹🇳' },
  'FR': { dialCode: '+33', flag: '🇫🇷' },
  'ES': { dialCode: '+34', flag: '🇪🇸' },
  'BE': { dialCode: '+32', flag: '🇧🇪' },
  'CA': { dialCode: '+1', flag: '🇨🇦' },
  'US': { dialCode: '+1', flag: '🇺🇸' },
  'GB': { dialCode: '+44', flag: '🇬🇧' },
  'DE': { dialCode: '+49', flag: '🇩🇪' },
  'IT': { dialCode: '+39', flag: '🇮🇹' },
  'NL': { dialCode: '+31', flag: '🇳🇱' },
  'PT': { dialCode: '+351', flag: '🇵🇹' },
  'SA': { dialCode: '+966', flag: '🇸🇦' },
  'AE': { dialCode: '+971', flag: '🇦🇪' },
  'EG': { dialCode: '+20', flag: '🇪🇬' },
  'SN': { dialCode: '+221', flag: '🇸🇳' },
  'CI': { dialCode: '+225', flag: '🇨🇮' },
  'ML': { dialCode: '+223', flag: '🇲🇱' },
  'MR': { dialCode: '+222', flag: '🇲🇷' },
};

// Helper to format phone with country code
const formatPhoneWithCountry = (phone?: string, countryCode?: string): string => {
  if (!phone) return '—';

  // If phone already starts with + or 00, it already has the dial code - don't add another
  if (phone.startsWith('+') || phone.startsWith('00')) {
    // Try to find the country flag based on the dial code in the phone
    for (const [, data] of Object.entries(COUNTRY_PHONE_CODES)) {
      if (phone.startsWith(data.dialCode)) {
        return `${data.flag} ${phone}`;
      }
    }
    return phone;
  }

  const country = countryCode ? COUNTRY_PHONE_CODES[countryCode] : null;
  if (country) {
    return `${country.flag} ${country.dialCode} ${phone}`;
  }
  return phone;
};

// Emergency contact relationship labels
// Helper to get relationship label
const getRelationshipLabel = (relation?: string): string => emergencyRelationLabel(relation) || '—';

// Section header component
const SectionHeader: React.FC<{
  title: string;
  icon: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
}> = ({ title, icon, expanded, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    className="w-full flex items-center justify-between px-6 py-4 bg-gray-50 hover:bg-gray-100 transition-colors"
  >
    <div className="flex items-center gap-3">
      {icon}
      <span className="font-medium text-gray-900">{title}</span>
    </div>
    {expanded ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
  </button>
);

// Field display component
const Field: React.FC<{ label: string; value: string; className?: string }> = ({ label, value, className = '' }) => (
  <div className={className}>
    <dt className="text-sm text-gray-500 mb-1">{label}</dt>
    <dd className="text-gray-900 font-medium">{value}</dd>
  </div>
);

// Labels for select values
const sexeLabels: Record<string, string> = { M: 'Masculin', F: 'Féminin' };
const handicapLabels: Record<string, string> = {
  N: 'Aucun',
  A: 'Auditif',
  M: 'Moteur',
  V: 'Visuel',
  AM: 'Auditif et Moteur',
  AV: 'Auditif et Visuel',
  MV: 'Moteur et Visuel',
  T: 'Tous',
  XX: 'Autre',
};
const statutProfesseurLabels: Record<string, string> = {
  P: 'Permanent',
  V: 'Vacataire',
  PERMANENT: 'Permanent',
  VACATAIRE: 'Vacataire',
};

// Document type labels (French) - matching DocumentUpload nomenclature
const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  CIN_FRONT: 'Carte d\'identité (Recto)',
  CIN_BACK: 'Carte d\'identité (Verso)',
  PASSPORT: 'Passeport',
  CV: 'CV',
  DIPLOME: 'Diplôme',
  ENGAGEMENT: 'Lettre d\'engagement',
  AUTORISATION: 'Autorisation d\'enseignement',
  ATTESTATION_TRAVAIL: 'Attestation de travail',
  PHOTO: 'Photo d\'identité',
  RIB: 'RIB',
  ATTESTATION_INSCRIPTION: 'Attestation d\'inscription',
  RELEVE_NOTES: 'Relevé de notes',
  CERTIFICAT_SCOLARITE: 'Certificat de scolarité',
};

export const StaffProfileView: React.FC<StaffProfileViewProps> = ({
  data,
  showChangeRequest = false,
  isProfessor = false,
  signatureStatus,
  departementName,
  gradeName,
  diplomeName,
  payrollCampusName,
  documents = [],
  isArchived = false,
  archivedAt,
}) => {
  const [expandedSections, setExpandedSections] = useState({
    identity: true,
    coordinates: true,
    emergency: true,
    professional: true,
    professor: true,
    signatures: true,
    documents: true,
  });

  const [showChangeRequestModal, setShowChangeRequestModal] = useState(false);
  const [changeRequestsKey, setChangeRequestsKey] = useState(0);

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Format salary for display
  const formatSalary = (salary: unknown): string => {
    if (salary === undefined || salary === null || salary === '') return '—';
    const numValue = Number(salary);
    if (isNaN(numValue)) return String(salary);
    return `${numValue.toLocaleString('fr-FR')} DH`;
  };

  // Format taux horaire for display
  const formatTauxHoraire = (taux: unknown): string => {
    if (taux === undefined || taux === null || taux === '') return '—';
    const numValue = Number(taux);
    if (isNaN(numValue)) return String(taux);
    return `${numValue.toLocaleString('fr-FR')} DH/heure`;
  };

  // Check if user is from public institution
  const isFromPublicInstitution = data.estOriginaireEtablissementPublic === true ||
    data.estOriginaireEtablissementPublic === 'true' ||
    data.estOriginaireEtablissementPublic === 'O';

  return (
    <div className="space-y-4">
      {/* Change Request Button - Fixed at top */}
      {showChangeRequest && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 rounded-lg">
                <Edit3 className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Besoin de modifier une information ?</h3>
                <p className="text-sm text-gray-500">Soumettez une demande qui sera examinée par l'administration</p>
              </div>
            </div>
            <button
              onClick={() => setShowChangeRequestModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Edit3 className="w-4 h-4" />
              Demander une modification
            </button>
          </div>
        </div>
      )}

      {/* Archive Status Banner */}
      {isArchived && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Briefcase className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h3 className="font-medium text-orange-800">Compte archive</h3>
              <p className="text-sm text-orange-600">
                Ce membre du personnel a ete archive
                {archivedAt && (
                  <> le <span className="font-medium">{new Date(archivedAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span></>
                )}
                . Il n'apparait plus dans la paie des mois suivants.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Section 1: Identité */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <SectionHeader
          title="Section 1: Identité"
          icon={<User className="w-5 h-5 text-primary-600" />}
          expanded={expandedSections.identity}
          onToggle={() => toggleSection('identity')}
        />
        {expandedSections.identity && (
          <div className="p-6">
            <dl className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Field label="Nom" value={formatValue(data.nom)} />
              <Field label="Prénom" value={formatValue(data.prenom)} />
              <Field label="Nom (Arabe)" value={formatValue(data.nomArabe)} />
              <Field label="Prénom (Arabe)" value={formatValue(data.prenomArabe)} />
              {/* Show CIN or Passport based on which one has data */}
              {data.cnie ? (
                <Field label="N° CIN" value={formatValue(data.cnie)} />
              ) : data.passport ? (
                <Field label="N° Passeport" value={formatValue(data.passport)} />
              ) : (
                <Field label="N° CIN / Passeport" value="—" />
              )}
              <Field label="Sexe" value={sexeLabels[data.sexe || ''] || formatValue(data.sexe)} />
              <Field label="Date de naissance" value={toDisplayDate(data.dateNaissance) || formatValue(data.dateNaissance)} />
              <Field label="Pays d'origine" value={getCountryName(data.paysOrigine)} />
              <Field label="Situation de handicap" value={handicapLabels[data.handicap || ''] || formatValue(data.handicap)} />
            </dl>
          </div>
        )}
      </div>

      {/* Section 2: Coordonnées */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <SectionHeader
          title="Section 2: Coordonnées"
          icon={<Phone className="w-5 h-5 text-primary-600" />}
          expanded={expandedSections.coordinates}
          onToggle={() => toggleSection('coordinates')}
        />
        {expandedSections.coordinates && (
          <div className="p-6">
            <dl className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Field label="Email" value={formatValue(data.email)} />
              <Field label="Téléphone" value={formatPhoneWithCountry(data.telephone as string, data.phoneCountry as string)} />
              <Field label="Adresse" value={formatValue(data.adresse)} className="md:col-span-2 lg:col-span-3" />
            </dl>
          </div>
        )}
      </div>

      {/* Section 3: Contact d'Urgence */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <SectionHeader
          title="Section 3: Contact d'Urgence"
          icon={<Users className="w-5 h-5 text-primary-600" />}
          expanded={expandedSections.emergency}
          onToggle={() => toggleSection('emergency')}
        />
        {expandedSections.emergency && (
          <div className="p-6">
            <dl className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Field label="Nom du contact" value={formatValue(data.urgenceNom)} />
              <Field label="Relation" value={getRelationshipLabel(data.urgenceRelation as string)} />
              <Field label="Téléphone" value={formatPhoneWithCountry(data.urgenceNumero as string, data.urgencePhoneCountry as string)} />
              <Field label="Adresse" value={formatValue(data.urgenceAdresse)} className="md:col-span-2 lg:col-span-3" />
            </dl>
          </div>
        )}
      </div>

      {/* Section 4: Informations Professionnelles */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <SectionHeader
          title="Section 4: Informations Professionnelles"
          icon={<Briefcase className="w-5 h-5 text-primary-600" />}
          expanded={expandedSections.professional}
          onToggle={() => toggleSection('professional')}
        />
        {expandedSections.professional && (
          <div className="p-6">
            <dl className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Field label="Fonction" value={formatValue(data.fonction)} />
              <Field label="Diplôme" value={diplomeName || formatValue(data.diplomeId)} />
              <Field label="Date de recrutement" value={toDisplayDate(data.dateRecrutement) || formatValue(data.dateRecrutement)} />
              <Field
                label="RIB"
                value={formatValue(data.rib)}
                className="lg:col-span-2"
              />
              <Field label="Campus de Paie" value={payrollCampusName || formatValue((data as Record<string, unknown>).payrollCampusId)} />
              <Field label="Salaire mensuel net" value={formatSalary(data.salaireMensuelNet)} />
              {/* Show isAlsoProfessor for non-teacher staff */}
              {!isProfessor && (
                <Field
                  label="Êtes-vous également professeur/enseignant ?"
                  value={(data as Record<string, unknown>).isAlsoProfessor === true ? 'Oui' : 'Non'}
                />
              )}
            </dl>
          </div>
        )}
      </div>

      {/* Section 5: Informations Enseignant (Professors or staff who also teach) */}
      {(isProfessor || (data as Record<string, unknown>).isAlsoProfessor === true) && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <SectionHeader
            title="Section 5: Informations Enseignant"
            icon={<GraduationCap className="w-5 h-5 text-primary-600" />}
            expanded={expandedSections.professor}
            onToggle={() => toggleSection('professor')}
          />
          {expandedSections.professor && (
            <div className="p-6">
              <dl className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Field
                  label="Statut"
                  value={statutProfesseurLabels[data.statutProfesseur as string || ''] || formatValue(data.statutProfesseur)}
                />
                <Field label="Département d'attache" value={departementName || formatValue(data.departementAttacheId)} />
                <Field label="Grade" value={gradeName || formatValue(data.gradeId)} />
                <Field label="Spécialité" value={formatValue(data.specialite)} />
                <Field label="Matières enseignées" value={formatValue(data.matieresEnseignees)} className="md:col-span-2" />
                <Field label="Taux horaire" value={formatTauxHoraire(data.tauxHoraire)} />
                <Field
                  label="Originaire d'un établissement public"
                  value={isFromPublicInstitution ? 'Oui' : 'Non'}
                />
                {isFromPublicInstitution && (
                  <Field
                    label="Établissement d'origine"
                    value={formatValue(data.etablissementOrigine)}
                    className="md:col-span-2"
                  />
                )}
              </dl>
            </div>
          )}
        </div>
      )}

      {/* Section: Signatures Électroniques */}
      {signatureStatus?.reglementInterieurSigned && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <SectionHeader
            title="Documents Signés Électroniquement"
            icon={<Shield className="w-5 h-5 text-primary-600" />}
            expanded={expandedSections.signatures}
            onToggle={() => toggleSection('signatures')}
          />
          {expandedSections.signatures && (
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                Ces documents ont été signés électroniquement lors de la soumission de votre profil.
                Les signatures sont horodatées et constituent une preuve d&apos;acceptation.
              </p>
              <div className="space-y-4">
                {/* Règlement Intérieur */}
                <div className="flex items-start gap-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-gray-900">Règlement Intérieur</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Signé le{' '}
                      <span className="font-medium text-gray-900">
                        {new Date(signatureStatus.reglementInterieurSignedAt!).toLocaleString('fr-FR', {
                          dateStyle: 'full',
                          timeStyle: 'short',
                        })}
                      </span>
                    </p>
                    {signatureStatus.reglementInterieurVersionId && (
                      <p className="text-xs text-gray-500 mt-1">
                        Version du document: {signatureStatus.reglementInterieurVersionId.slice(0, 8)}...
                      </p>
                    )}
                  </div>
                </div>

                {/* Consentement Médias */}
                {signatureStatus.consentementMediasSigned ? (
                  <div className="flex items-start gap-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-gray-900">Consentement Réseaux Sociaux</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {signatureStatus.consentementMediasAccepted ? (
                          <>
                            Accepté le{' '}
                            <span className="font-medium text-gray-900">
                              {new Date(signatureStatus.consentementMediasSignedAt!).toLocaleString('fr-FR', {
                                dateStyle: 'full',
                                timeStyle: 'short',
                              })}
                            </span>
                          </>
                        ) : (
                          'Refusé'
                        )}
                      </p>
                      {signatureStatus.consentementMediasVersionId && (
                        <p className="text-xs text-gray-500 mt-1">
                          Version du document: {signatureStatus.consentementMediasVersionId.slice(0, 8)}...
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="w-5 h-5 border-2 border-gray-300 rounded-full mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-gray-700">Consentement Réseaux Sociaux</h4>
                      <p className="text-sm text-gray-500 mt-1">Non signé (optionnel)</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Section: Documents soumis */}
      {documents.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <SectionHeader
            title="Documents soumis"
            icon={<FileText className="w-5 h-5 text-primary-600" />}
            expanded={expandedSections.documents}
            onToggle={() => toggleSection('documents')}
          />
          {expandedSections.documents && (
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                Voici les documents que vous avez soumis lors de l'inscription. Pour demander une modification,
                veuillez utiliser le bouton "Demander une modification" en haut de la page.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center gap-3 p-4 bg-gray-50 border border-gray-200 rounded-lg"
                  >
                    <div className="p-2 bg-primary-100 rounded-lg flex-shrink-0">
                      <FileText className="w-5 h-5 text-primary-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {DOCUMENT_TYPE_LABELS[doc.documentType] || doc.documentType}
                      </p>
                      <p className="text-sm text-gray-500 truncate">{doc.fileName}</p>
                      {doc.isVerified && (
                        <span className="inline-flex items-center gap-1 text-xs text-green-600 mt-1">
                          <CheckCircle className="w-3 h-3" />
                          Vérifié
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* View Document */}
                      <a
                        href={doc.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                        title="Voir le document"
                      >
                        <Eye className="w-4 h-4" />
                      </a>
                      {/* Download Document */}
                      <a
                        href={doc.fileUrl}
                        download={doc.fileName}
                        className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                        title="Télécharger"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Change Requests Section */}
      {showChangeRequest && (
        <ChangeRequestsSection
          key={changeRequestsKey}
          onRefresh={() => setChangeRequestsKey(prev => prev + 1)}
        />
      )}

      {/* Change Request Modal */}
      <StaffChangeRequestModal
        isOpen={showChangeRequestModal}
        onClose={() => setShowChangeRequestModal(false)}
        staffData={data as Record<string, unknown>}
        isProfessor={isProfessor}
        onSuccess={() => {
          setChangeRequestsKey(prev => prev + 1);
        }}
      />
    </div>
  );
};

export default StaffProfileView;
